import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { AdminNav } from "@/components/admin/AdminNav";

interface AdminOpp {
  id: string;
  kind: string;
  title: string;
  url: string | null;
  organization: string | null;
  amount_cents: number | null;
  deadline_at: number | null;
  geo: string | null;
  confidence_score: number | null;
  urgency_score: number | null;
  dismissed_globally: number;
  source_name: string | null;
  expired: number;
}

type Filter = "all" | "low_confidence" | "dismissed";

export function AdminOpportunities() {
  const api = useApi();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("all");

  const list = useQuery({
    queryKey: ["admin-opps", filter],
    queryFn: () => api.get<{ opportunities: AdminOpp[] }>(`/admin/opportunities?filter=${filter}`),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/opportunities/${id}/dismiss`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-opps"] }),
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/opportunities/${id}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-opps"] }),
  });

  const opps = list.data?.opportunities ?? [];

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-8">
        <p className="label-eyebrow">Operations</p>
        <h1 className="mt-2 text-display-2 font-display">Opportunities</h1>
        <p className="mt-3 text-whisper text-sm max-w-xl leading-relaxed">
          Review extracted opportunities and dismiss low-quality entries from
          the global feed.
        </p>
      </header>

      <AdminNav />

      <div className="mb-6 flex items-center gap-5 text-[10px] uppercase tracking-[0.18em]">
        {(["all", "low_confidence", "dismissed"] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`pb-2 border-b transition-colors ${
              filter === f
                ? "text-fog border-gold-glow"
                : "text-whisper border-transparent hover:text-fog"
            }`}
          >
            {f.replace("_", " ")}
          </button>
        ))}
      </div>

      {list.isLoading ? (
        <p className="text-sm text-whisper">Reading opportunities…</p>
      ) : opps.length === 0 ? (
        <p className="text-sm text-whisper">Nothing in this view.</p>
      ) : (
        <div className="panel divide-y divide-veil/40">
          {opps.map((o) => {
            const conf = o.confidence_score ?? 0;
            return (
              <div
                key={o.id}
                className="grid grid-cols-[1fr_auto] items-start gap-6 px-6 py-4"
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
                    {o.kind}
                    {o.organization && <span> · {o.organization}</span>}
                    {o.geo && <span> · {o.geo}</span>}
                    {o.source_name && <span className="text-whisper"> · via {o.source_name}</span>}
                  </div>
                  <div className="mt-1 font-display text-sm leading-snug">
                    {o.url ? (
                      <a
                        href={o.url}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-gold-glow transition-colors"
                      >
                        {o.title}
                      </a>
                    ) : (
                      o.title
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-[10px] uppercase tracking-[0.18em]">
                    <span
                      className={
                        conf < 0.5 ? "text-gold-glow" : "text-whisper"
                      }
                    >
                      confidence {(conf * 100).toFixed(0)}%
                    </span>
                    {o.expired === 1 && <span className="text-muted">expired</span>}
                    {o.dismissed_globally === 1 && (
                      <span className="text-muted">dismissed</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {o.dismissed_globally === 1 ? (
                    <button
                      type="button"
                      onClick={() => restore.mutate(o.id)}
                      disabled={restore.isPending}
                      className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors disabled:opacity-40"
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => dismiss.mutate(o.id)}
                      disabled={dismiss.isPending}
                      className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors disabled:opacity-40"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
